import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from 'axios';
// html2canvas removed — replaced with fast Canvas 2D API in captureLabel()
import { createPortal } from "react-dom";
import './labelDesigner.css';
import Icon from './components/icon';


const compressImage = (base64Str, maxWidth = 2400, maxHeight = 2400) => {
    if (!base64Str) return Promise.resolve('');
    if (base64Str.startsWith('data:image/svg+xml;base64,')) {
        return Promise.resolve(base64Str);
    }
    return new Promise((resolve) => {
        const img = new Image();
        img.src = base64Str;
        img.onload = () => {
            let width = img.width;
            let height = img.height;
            if (width <= maxWidth && height <= maxHeight) {
                return resolve(base64Str);
            }
            if (width > maxWidth || height > maxHeight) {
                if (width > height) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                } else {
                    width = Math.round((width * maxHeight) / height);
                    height = maxHeight;
                }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/png', 1.0));
        };
        img.onerror = () => resolve(base64Str);
    });
};

const t = (key, defaultText = '') => {
    if (window.cld_ajax_obj && window.cld_ajax_obj.translations) {
        // Direct match
        if (window.cld_ajax_obj.translations[key]) {
            return window.cld_ajax_obj.translations[key];
        }
        // Normalize key
        const norm = String(key).trim().toLowerCase().replace(/[^a-z0-9]/g, '_').replace(/_+/g, '_');
        if (window.cld_ajax_obj.translations[norm]) {
            return window.cld_ajax_obj.translations[norm];
        }
    }
    return defaultText || key;
};

const LGC_COLORS = {
    'W&B': [
        { code: 'LGC-WB-01', hex: '#FFFFFF', name: 'White' },
        { code: 'LGC-WB-02', hex: '#E5E7EB', name: 'Gray 1' },
        { code: 'LGC-WB-03', hex: '#D1D5DB', name: 'Gray 2' },
        { code: 'LGC-WB-04', hex: '#9CA3AF', name: 'Gray 3' },
        { code: 'LGC-WB-05', hex: '#4B5563', name: 'Gray 4' },
        { code: 'LGC-WB-06', hex: '#000000', name: 'Black' },
    ],
    Beige: [
        { code: 'LGC-BE-01', hex: '#FFF7E6', name: 'Ivory' },
        { code: 'LGC-BE-02', hex: '#F5E6CC', name: 'Light Beige' },
        { code: 'LGC-BE-03', hex: '#E2C9A6', name: 'Sand' },
        { code: 'LGC-BE-04', hex: '#CBB292', name: 'Khaki' },
        { code: 'LGC-BE-05', hex: '#B08D57', name: 'Bronze' },
        { code: 'LGC-BE-06', hex: '#8B6B3A', name: 'Dark Beige' },
    ],
    Yellow: [
        { code: 'LGC-YE-01', hex: '#FFF9C4', name: 'Lemon' },
        { code: 'LGC-YE-02', hex: '#FFF176', name: 'Pastel Yellow' },
        { code: 'LGC-YE-03', hex: '#FFEE58', name: 'Yellow' },
        { code: 'LGC-YE-04', hex: '#FBC02D', name: 'Amber' },
        { code: 'LGC-YE-05', hex: '#F9A825', name: 'Goldenrod' },
        { code: 'LGC-YE-06', hex: '#F57F17', name: 'Dark Yellow' },
    ],
    Orange: [
        { code: 'LGC-OR-01', hex: '#FFE0B2', name: 'Peach' },
        { code: 'LGC-OR-02', hex: '#FFCC80', name: 'Light Orange' },
        { code: 'LGC-OR-03', hex: '#FFB74D', name: 'Orange' },
        { code: 'LGC-OR-04', hex: '#FB8C00', name: 'Tangerine' },
        { code: 'LGC-OR-05', hex: '#F57C00', name: 'Deep Orange' },
        { code: 'LGC-OR-06', hex: '#E65100', name: 'Dark Orange' },
    ],
    Red: [
        { code: 'LGC-RE-01', hex: '#FFCDD2', name: 'Rose' },
        { code: 'LGC-RE-02', hex: '#EF9A9A', name: 'Light Red' },
        { code: 'LGC-RE-03', hex: '#E57373', name: 'Red' },
        { code: 'LGC-RE-04', hex: '#EF5350', name: 'Crimson' },
        { code: 'LGC-RE-05', hex: '#E53935', name: 'Scarlet' },
        { code: 'LGC-RE-06', hex: '#B71C1C', name: 'Dark Red' },
    ],
    Pink: [
        { code: 'LGC-PI-01', hex: '#F8BBD0', name: 'Blush' },
        { code: 'LGC-PI-02', hex: '#F48FB1', name: 'Light Pink' },
        { code: 'LGC-PI-03', hex: '#F06292', name: 'Pink' },
        { code: 'LGC-PI-04', hex: '#EC407A', name: 'Magenta' },
        { code: 'LGC-PI-05', hex: '#D81B60', name: 'Raspberry' },
        { code: 'LGC-PI-06', hex: '#880E4F', name: 'Dark Pink' },
    ],
    Purple: [
        { code: 'LGC-PU-01', hex: '#E1BEE7', name: 'Lavender' },
        { code: 'LGC-PU-02', hex: '#CE93D8', name: 'Light Purple' },
        { code: 'LGC-PU-03', hex: '#BA68C8', name: 'Purple' },
        { code: 'LGC-PU-04', hex: '#8E24AA', name: 'Violet' },
        { code: 'LGC-PU-05', hex: '#7B1FA2', name: 'Deep Purple' },
        { code: 'LGC-PU-06', hex: '#4A148C', name: 'Dark Purple' },
    ],
    Blue: [
        { code: 'LGC-BL-01', hex: '#BBDEFB', name: 'Powder Blue' },
        { code: 'LGC-BL-02', hex: '#90CAF9', name: 'Light Blue' },
        { code: 'LGC-BL-03', hex: '#64B5F6', name: 'Blue' },
        { code: 'LGC-BL-04', hex: '#1E88E5', name: 'Royal Blue' },
        { code: 'LGC-BL-05', hex: '#1565C0', name: 'Indigo Blue' },
        { code: 'LGC-BL-06', hex: '#0D47A1', name: 'Dark Blue' },
    ],
    Green: [
        { code: 'LGC-GR-01', hex: '#C8E6C9', name: 'Mint' },
        { code: 'LGC-GR-02', hex: '#A5D6A7', name: 'Light Green' },
        { code: 'LGC-GR-03', hex: '#81C784', name: 'Green' },
        { code: 'LGC-GR-04', hex: '#43A047', name: 'Forest Green' },
        { code: 'LGC-GR-05', hex: '#2E7D32', name: 'Pine' },
        { code: 'LGC-GR-06', hex: '#1B5E20', name: 'Dark Green' },
    ],
    Brown: [
        { code: 'LGC-BR-01', hex: '#D7CCC8', name: 'Light Brown' },
        { code: 'LGC-BR-02', hex: '#BCAAA4', name: 'Beige Brown' },
        { code: 'LGC-BR-03', hex: '#A1887F', name: 'Brown' },
        { code: 'LGC-BR-04', hex: '#8D6E63', name: 'Umber' },
        { code: 'LGC-BR-05', hex: '#6D4C41', name: 'Walnut' },
        { code: 'LGC-BR-06', hex: '#4E342E', name: 'Dark Brown' },
    ],
    Neon: [
        { code: 'LGC-NE-01', hex: '#CCFF00', name: 'Neon Yellow' },
        { code: 'LGC-NE-02', hex: '#39FF14', name: 'Neon Green' },
        { code: 'LGC-NE-03', hex: '#FF5E00', name: 'Neon Orange' },
        { code: 'LGC-NE-04', hex: '#FF1493', name: 'Neon Pink' },
    ],
};

const LGC_CATEGORIES = ['W&B', 'Beige', 'Yellow', 'Orange', 'Red', 'Pink', 'Purple', 'Blue', 'Green', 'Brown', 'Neon'];

const getLgcInfoByHex = (hex) => {
    if (!hex) return null;
    const cleanHex = hex.toUpperCase();
    for (const cat of LGC_CATEGORIES) {
        const match = LGC_COLORS[cat].find(c => c.hex.toUpperCase() === cleanHex);
        if (match) return match;
    }
    return null;
};

export default function LabelDesigner() {
    const [text, setText] = useState("");
    const [font, setFont] = useState("Arial");
    const [fontCategory, setFontCategory] = useState("All");
    const activeQuantities = useMemo(() => {
        const allQuantities = [250, 500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 6000, 7000, 8000, 9000, 10000];
        if (!window.cld_ajax_obj || !window.cld_ajax_obj.settings) {
            return allQuantities;
        }
        const settings = window.cld_ajax_obj.settings;
        return allQuantities.filter(q => {
            const activeKey = `quantity_${q}_active`;
            return settings[activeKey] === undefined || String(settings[activeKey]) !== '0';
        });
    }, []);

    const [textColor, setTextColor] = useState("#000000");
    const [bgColor, setBgColor] = useState("#ffffff");
    const [icon, setIcon] = useState("none");
    const [iconCategory, setIconCategory] = useState("All");
    const [iconPosition, setIconPosition] = useState("none");
    const [labelWidth, setLabelWidth] = useState(60);
    const [labelHeight, setLabelHeight] = useState(24);
    const [quantity, setQuantity] = useState(() => {
        return activeQuantities.length > 0 ? activeQuantities[0] : 1000;
    });
    const [loading, setLoading] = useState(false);
    const [frameStyle, setFrameStyle] = useState('none');
    const [applicationMethod, setApplicationMethod] = useState('sew-on');
    const [fontSize, setFontSize] = useState(24);
    const [labelType, setLabelType] = useState('woven');
    const [fabricType, setFabricType] = useState('satin');
    const [extras, setExtras] = useState({ standard: true, zemindouble: false, ultrasonicCutting: false });
    const [textAlignment, setTextAlignment] = useState('middle-center');
    const [selectedQuality, setSelectedQuality] = useState('standard');
    const [overlay, setOverlay] = useState({ show: false, message: '', onConfirm: null });
    const [scrollPosition, setScrollPosition] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
    const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
    const [activeMobileTab, setActiveMobileTab] = useState('type_size');
    const [isPreviewZoomed, setIsPreviewZoomed] = useState(false);
    const [openColorPicker, setOpenColorPicker] = useState(null); // 'text' | 'bg' | null
    const [activeColorCategory, setActiveColorCategory] = useState('W&B');

    useEffect(() => {
        const onDocClick = (e) => {
            if (!openColorPicker) return;
            const el = e.target;
            if (el && el.closest && (el.closest('.color-popover') || el.closest('.color-swatch-button'))) {
                return;
            }
            setOpenColorPicker(null);
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [openColorPicker]);

    const categorySliderRef = useRef(null);
    const labelRef = useRef(null);
    const fontCategorySliderRef = useRef(null);
    const [fontScrollPosition, setFontScrollPosition] = useState(0);

    // Default quantity selection based on active ones
    useEffect(() => {
        if (activeQuantities.length > 0 && !activeQuantities.includes(quantity)) {
            setQuantity(activeQuantities[0]);
        }
    }, [activeQuantities, quantity]);

    // Mobil cihaz kontrolü
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1200);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Desktop'a geçişte modal'ı kapat
    useEffect(() => {
        if (!isMobile && isMobilePreviewOpen) {
            setIsMobilePreviewOpen(false);
        }
    }, [isMobile, isMobilePreviewOpen]);

    const fontCategories = useMemo(() => {
        // Önce orijinal kategorileri tanımla
        const baseCategories = {
            "Sans Serif": [
                "Roboto", // Temiz, modern, çok yönlü
                "Open Sans", // Okunabilir, sade
                "Montserrat", // Geometrik, şık
                "Poppins", // Yuvarlak, dostane
                "Inter", // UI için optimize, net
                "Lato", // Dengeli, profesyonel
                "Rubik", // Hafif eğlenceli, modern
                "Work Sans", // Hafif ince, çağdaş
                "Nunito", // Yuvarlak, samimi
                "Source Sans 3", // Çok yönlü, okunabilir
                "Barlow", // Modern, hafif kondense
                "Public Sans", // Resmi, temiz
            ],
            "Serif": [
                "Playfair Display", // Zarif, dekoratif
                "Merriweather", // Okunabilir, klasik
                "Lora", // Yumuşak, edebi
                "Crimson Pro", // Sofistike, tarihsel
                "Libre Baskerville", // Kitaplar için ideal
                "EB Garamond", // Rönesans esintili
                "Zilla Slab", // Modern serif, güçlü
                "Cardo", // Akademik, zarif
                "Bitter", // Kalın, modern serif
                "PT Serif", // Dengeli, klasik
                "Vollkorn", // Sağlam, tarihsel
                "Source Serif 4", // Çok yönlü, okunabilir
            ],
            "Handwriting": [
                "Caveat", // Rahat, el yazısı tarzı
                "Dancing Script", // Akıcı, zarif
                "Patrick Hand", // Samimi, düzgün
                "Indie Flower", // Çizgili, eğlenceli
                "Kalam", // Kalın, doğal
                "Shadows Into Light", // İnce, kişisel
                "Amatic SC", // El yazısı, cesur
                "Architects Daughter", // Sevimli, casual
                "Gochi Hand", // Çocukça, eğlenceli
                "Annie Use Your Telescope", // El yazısı, samimi
                "Coming Soon", // İnce, rahat
                "Nothing You Could Do", // Fırça tarzı, doğal
            ],
            "Decorative": [
                "Bebas Neue", // Kalın, dramatik
                "Oswald", // Kondense, güçlü
                "Lobster", // Retro, süslü
                "Abril Fatface", // Art deco, çarpıcı
                "Bangers", // Çizgi roman tarzı
                "Alfa Slab One", // Kalın, slab serif
                "Shrikhand", // Etnik, dekoratif
                "Fredericka the Great", // Gotik, süslü
                "Black Han Sans", // Kalın, sans-serif benzeri
                "Big Shoulders", // Geometrik, endüstriyel
                "Passion One", // Enerjik, yuvarlak
                "Rammetto One", // Eğlenceli, dekoratif
            ],
            "Modern": [
                "Futura", // Geometrik, fütüristik
                "Manrope", // Minimal, yuvarlak
                "Overpass", // Teknolojik, temiz
                "Jost", // Modern, sofistike
                "Sora", // İnce, çağdaş
                "Outfit", // Moda odaklı, zarif
                "Chivo", // Net, profesyonel
                "Karla", // Minimalist, dostane
                "Archivo", // Geometrik, modern
                "DM Sans", // Temiz, çok yönlü
                "Exo 2", // Teknolojik, yuvarlak
                "Cabin", // Modern, dengeli
            ],
            "Vintage": [
                "Special Elite", // Daktilo tarzı
                "Old Standard TT", // Antik, klasik
                "Cinzel", // Roma esintili
                "IM Fell English", // Tarihsel, el yazması
                "UnifrakturMaguntia", // Gotik, eski Alman
                "Spectral", // Retro, zarif
                "Rye", // Vahşi batı tarzı
                "Almendra", // Ortaçağ esintili
                "Pirata One", // Korsan, dekoratif
                "Metal Mania", // Gotik, heavy metal
                "Germania One", // Alman, tarihsel
                "Caesar Dressing", // Antik Roma esintili
            ],
            "Script": [
                "Great Vibes", // Zarif, kaligrafik
                "Sacramento", // İnce, akıcı
                "Alex Brush", // Romantik, fırça tarzı
                "Kaushan Script", // Enerjik, el yazısı
                "Parisienne", // Fransız, zarif
                "Yellowtail", // Modern kaligrafi
                "Tangerine", // İnce, süslü
                "Clicker Script", // Fırça, dekoratif
                "Allura", // Akıcı, zarif
                "Bad Script", // Düzensiz, el yazısı
                "Lovers Quarrel", // Romantik, süslü
                "Cedarville Cursive", // İnce, kıvrımlı
            ],
            "Monospace": [
                "Fira Code", // Kodlama için optimize
                "IBM Plex Mono", // Modern, net
                "Space Mono", // Geometrik, retro
                "Inconsolata", // Okunabilir, sade
                "Roboto Mono", // Temiz, profesyonel
                "Source Code Pro", // Adobe tasarımı, net
                "JetBrains Mono", // Programlama dostu
                "Courier Prime", // Klasik daktilo tarzı
                "Overpass Mono", // Modern, teknolojik
                "Ubuntu Mono", // Yuvarlak, okunabilir
                "VT323", // Retro, dijital
                "Share Tech Mono", // Teknolojik, net
            ],
        };

        // Tüm fontları tek bir dizide topla
        const allFonts = [...new Set(Object.values(baseCategories).flat())];

        // All kategorisini ekle ve diğer kategorilerle birleştir
        return {
            "All": allFonts,
            ...baseCategories
        };
    }, []);

    const iconCategories = useMemo(() => {
        const categories = [
            {
                name: 'Letters',
                icon: 'a',
                icons: ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z']
            },
            {
                name: 'Numbers',
                icon: 'circle-number-1',
                icons: ['circle-number-0', 'circle-number-1', 'circle-number-2', 'circle-number-3', 'circle-number-4', 'circle-number-5',
                    'circle-number-6', 'circle-number-7', 'circle-number-8', 'circle-number-9', 'number-0', 'number-1', 'number-2',
                    'number-3', 'number-4', 'number-5', 'number-6', 'number-7', 'number-8', 'number-9', 'number-0-alt', 'number-1-alt',
                    'number-2-alt', 'number-3-alt', 'number-4-alt', 'number-5-alt', 'number-6-alt', 'number-7-alt', 'number-8-alt', 'number-9-alt']
            },
            {
                name: 'Arrows',
                icon: 'arrow-right',
                icons: ['arrow-down', 'arrow-down-from-line', 'arrow-down-left', 'arrow-down-right', 'arrow-down-short-wide',
                    'arrow-down-to-bracket', 'arrow-down-to-line', 'arrow-down-wide-short', 'arrow-left', 'arrow-left-arrow-right',
                    'arrow-left-from-line', 'arrow-left-to-line', 'arrow-right', 'arrow-right-from-bracket', 'arrow-right-from-line',
                    'arrow-right-to-bracket', 'arrow-right-to-line', 'arrow-rotate-left', 'arrow-rotate-right', 'arrow-trend-down',
                    'arrow-trend-up', 'arrow-turn-down-left', 'arrow-turn-down-right', 'arrow-turn-left-down', 'arrow-turn-left-up',
                    'arrow-turn-right-down', 'arrow-turn-right-up', 'arrow-turn-up-left', 'arrow-turn-up-right', 'arrow-up',
                    'arrows-left-right', 'arrows-repeat', 'arrows-rotate-clockwise', 'arrows-rotate-counter-clockwise']
            },
            {
                name: 'Shapes',
                icon: 'circle',
                icons: ['circle', 'circle-half', 'diamond', 'diamond-half', 'diamond-shape', 'hexagon', 'octagon', 'octagon-exclamation',
                    'square', 'square-checkmark', 'square-divide', 'square-equals', 'square-minus', 'square-plus', 'square-x',
                    'triangle', 'triangle-exclamation']
            },
            {
                name: 'Text Formatting',
                icon: 'align-left',
                icons: ['align-bottom', 'align-center-horizontal', 'align-center-vertical', 'align-left', 'align-right', 'align-text-center',
                    'align-text-justify', 'align-text-right', 'align-top', 'bold', 'italic', 'underline', 'cursor', 'cursor-click',
                    'grid', 'grid-masonry', 'maximize', 'minimize', 'sidebar-left', 'sidebar-right']
            },
            {
                name: 'Media',
                icon: 'microphone',
                icons: ['camera', 'camera-slash', 'desktop', 'film', 'headphones', 'image', 'images', 'laptop', 'microphone',
                    'microphone-slash', 'mobile', 'phone', 'phone-slash', 'tv', 'tv-retro', 'video', 'video-camera', 'video-camera-slash']
            },
            {
                name: 'Weather',
                icon: 'cloud',
                icons: ['cloud', 'cloud-arrow-down', 'cloud-arrow-up', 'cloud-fog', 'cloud-lightning', 'cloud-rain', 'cloud-snow',
                    'moon', 'moon-cloud', 'moon-fog', 'rainbow', 'rainbow-cloud', 'sun', 'sun-cloud', 'sun-fog', 'wind']
            },
            {
                name: 'Tools',
                icon: 'key',
                icons: ['book', 'book-open', 'bookmark', 'bookmark-plus', 'books', 'key', 'key-skeleton', 'keyboard',
                    'toolbox', 'wrench', 'pencil', 'pen-nib', 'palette', 'ruler', 'scissors']
            },
            {
                name: 'Food & Drink',
                icon: 'utensils',
                icons: ['bottle', 'cake', 'cake-slice', 'citrus-slice', 'cocktail', 'cupcake', 'ice-cream', 'mug',
                    'pizza', 'soda', 'utensils', 'wine-glass']
            },
            {
                name: 'Currency',
                icon: 'dollar',
                icons: ['british-pound', 'dollar', 'euro', 'yen', 'credit-card', 'money', 'receipt', 'wallet']
            },
            {
                name: 'Emojis',
                icon: 'face-smile',
                icons: ['face-angry', 'face-cry', 'face-laugh', 'face-meh', 'face-melt', 'face-no-mouth', 'face-open-mouth',
                    'face-sad', 'face-smile', 'person', 'person-walking', 'person-wave', 'user', 'users']
            },
            {
                name: 'Sports',
                icon: 'dice',
                icons: ['baseball', 'baseball-bat', 'basketball', 'dice', 'die-1', 'die-2', 'die-3', 'die-4', 'die-5',
                    'die-6', 'football', 'game-controller', 'hockey', 'joystick', 'soccer', 'tennis-ball']
            },
            {
                name: 'Zodiac',
                icon: 'star',
                icons: ['aquarius', 'aries', 'cancer', 'capricorn', 'gemini', 'leo', 'libra', 'pisces', 'sagittarius',
                    'scorpio', 'taurus', 'virgo', 'star', 'star-half']
            }
        ];

        const allIcons = [...new Set(categories.flatMap(cat => cat.icons))];
        return [
            {
                name: 'All',
                icon: 'star',
                icons: allIcons
            },
            ...categories
        ];
    }, []);

    const displayedIcons = useMemo(() => {
        const selectedCategory = iconCategories.find(cat => cat.name === iconCategory);
        return selectedCategory ? selectedCategory.icons : [];
    }, [iconCategory, iconCategories]);

    const scrollCategories = useCallback((direction) => {
        const container = categorySliderRef.current;
        if (container) {
            const scrollAmount = 200;
            const newPosition = direction === 'left'
                ? scrollPosition - scrollAmount
                : scrollPosition + scrollAmount;

            container.scrollTo({
                left: newPosition,
                behavior: 'smooth'
            });

            setScrollPosition(newPosition);
        }
    }, [scrollPosition]);

    const alignmentOptions = useMemo(() => [
        { value: 'top-left', label: t('top_left', 'Top Left'), preview: 'T' },
        { value: 'top-center', label: t('top_center', 'Top Center'), preview: 'T' },
        { value: 'top-right', label: t('top_right', 'Top Right'), preview: 'T' },
        { value: 'middle-left', label: t('middle_left', 'Middle Left'), preview: 'T' },
        { value: 'middle-center', label: t('middle_center', 'Middle Center'), preview: 'T' },
        { value: 'middle-right', label: t('middle_right', 'Middle Right'), preview: 'T' },
        { value: 'bottom-left', label: t('bottom_left', 'Bottom Left'), preview: 'T' },
        { value: 'bottom-center', label: t('bottom_center', 'Bottom Center'), preview: 'T' },
        { value: 'bottom-right', label: t('bottom_right', 'Bottom Right'), preview: 'T' }
    ], []);

    const iconPositions = useMemo(() => [
        { value: 'none', label: t('no_icon', 'No Icon'), preview: 'Text' },
        { value: 'left', label: t('left_icon', 'Left Icon'), preview: '★ Text' },
        { value: 'both', label: t('both_sides', 'Both Sides'), preview: '★ Text ★' },
        { value: 'right', label: t('right_icon', 'Right Icon'), preview: 'Text ★' }
    ], []);


    const sizeOptions = useMemo(() => [
        { name: t('small', 'Small'), width: 50, height: 19, label: 'Small\n100x40', info: t('best_for_small_items', 'Best for small items') },
        { name: t('medium', 'Medium'), width: 60, height: 24, label: 'Standard\n140x60', info: t('standard_size', 'Standard size') },
        { name: t('large', 'Large'), width: 70, height: 29, label: 'Büyük\n200x80', info: t('best_for_large_items', 'Best for large items') }
    ], []);

    const handleFontScroll = useCallback((e) => {
        setFontScrollPosition(e.currentTarget.scrollLeft);
    }, []);

    const scrollFontCategories = useCallback((direction) => {
        const container = fontCategorySliderRef.current;
        if (container) {
            const scrollAmount = 200;
            const currentScroll = container.scrollLeft;
            const newPosition = direction === 'left'
                ? currentScroll - scrollAmount
                : currentScroll + scrollAmount;

            container.scrollTo({
                left: newPosition,
                behavior: 'smooth'
            });

            setFontScrollPosition(newPosition);
        }
    }, []);

    const getFontSizeRange = useCallback(() => {
        let minSize, maxSize;
        if (labelWidth === 50 && labelHeight === 19) {
            minSize = 12;
            maxSize = 54;
        } else if (labelWidth === 60 && labelHeight === 24) {
            minSize = 18;
            maxSize = 78;
        } else if (labelWidth === 70 && labelHeight === 29) {
            minSize = 24;
            maxSize = 92;
        } else {
            minSize = 12;
            maxSize = 36;
        }
        return {
            min: minSize,
            max: maxSize
        };
    }, [labelWidth, labelHeight]);

    const calculateScaleFactor = useCallback((width, height) => {
        const checkMobile = window.innerWidth <= 1200;
        const targetMaxPx = checkMobile ? Math.min(220, window.innerWidth - 64) : 400;
        const referenceMm = 70; // Use fixed largest size as standard to allow proportional growth/shrink
        const uniform = targetMaxPx / referenceMm;
        return { widthScale: uniform, heightScale: uniform };
    }, []);
    const formatPrice = (price) => {
        // Ensure price is numeric
        const numericPrice = Number(price);
        if (isNaN(numericPrice)) {
            console.error('Invalid price value:', price);
            return '0';
        }

        // Read directly from localized WooCommerce object
        const {
            currency_symbol = '$',
            currency_position = 'left',
            decimal_separator = '.',
            thousand_separator = ',',
            decimals = 2
        } = window.cld_ajax_obj || {};

        // Fiyatı ondalık basamak sayısına göre yuvarla
        const formattedPrice = numericPrice.toFixed(decimals);

        // Ondalık ayracı dikkate alarak parçalara ayır
        const parts = formattedPrice.split('.');
        let integerPart = parts[0];
        const decimalPart = parts[1] || '';

        // Binlik ayracı ekle
        const regex = /\B(?=(\d{3})+(?!\d))/g;
        integerPart = integerPart.replace(regex, thousand_separator);

        // Ondalık kısmı birleştir
        const finalPrice = decimalPart ? `${integerPart}${decimal_separator}${decimalPart}` : integerPart;

        // Para birimi sembolünü pozisyona göre ekle
        switch (currency_position) {
            case 'left':
                return `${currency_symbol}${finalPrice}`;
            case 'right':
                return `${finalPrice}${currency_symbol}`;
            case 'left_space':
                return `${currency_symbol} ${finalPrice}`;
            case 'right_space':
            default:
                return `${finalPrice} ${currency_symbol}`;
        }
    };

    const calculatePrice = useCallback(() => {
        if (!window.cld_ajax_obj || !window.cld_ajax_obj.settings) {
            return { total: 0, perPiece: 0, discountRate: 0 };
        }

        const settings = window.cld_ajax_obj.settings;

        const safeNumber = (value) => {
            const num = Number(value);
            return isNaN(num) ? 0 : num;
        };

        let basePrice = 0;
        // Size based price
        if (labelWidth === 50 && labelHeight === 19) {
            basePrice = safeNumber(settings.price_small);
        } else if (labelWidth === 60 && labelHeight === 24) {
            basePrice = safeNumber(settings.price_medium);
        } else if (labelWidth === 70 && labelHeight === 29) {
            basePrice = safeNumber(settings.price_large);
        }

        // Quality fee
        if (labelType === 'woven') {
            switch (selectedQuality) {
                case 'standard':
                    basePrice += safeNumber(settings.quality_standard_fee);
                    break;
                case 'premium':
                    basePrice += safeNumber(settings.quality_premium_fee);
                    break;
                case 'eco':
                    basePrice += safeNumber(settings.quality_eco_fee);
                    break;
                default:
                    basePrice += safeNumber(settings.quality_standard_fee);
                    break;
            }
        }

        if (labelType === 'woven') {
            basePrice += safeNumber(settings.woven_extra_fee);
            if (applicationMethod === 'iron-on') {
                basePrice += safeNumber(settings.woven_iron_on_extra_fee);
            }
            if (extras && extras.zemindouble) {
                basePrice += safeNumber(settings.woven_zemin_double_fee);
            }
            if (extras && extras.ultrasonicCutting) {
                basePrice += safeNumber(settings.woven_ultrasonic_cutting_fee);
            }
        } else if (labelType === 'printed' && fabricType) {
            switch (fabricType) {
                case 'edge-satin':
                    basePrice += safeNumber(settings.fabric_edge_satin_fee);
                    break;
                case 'white-cotton':
                    basePrice += safeNumber(settings.fabric_white_cotton_fee);
                    break;
                case 'organic-cotton':
                    basePrice += safeNumber(settings.fabric_organic_cotton_fee);
                    break;
                case 'satin':
                    basePrice += safeNumber(settings.fabric_satin_fee);
                    break;
                default:
                    break;
            }
        }

        // Calculate discount
        const parseDiscountOrMarkup = (value) => {
            if (value === undefined || value === null) return { rate: 0, isMarkup: false };
            const strVal = String(value).trim();
            const markup = strVal.startsWith('+');
            const numStr = strVal.replace(/[^\d.]/g, '');
            const rate = parseFloat(numStr);
            return {
                rate: isNaN(rate) ? 0 : rate,
                isMarkup: markup
            };
        };

        const getDiscountData = () => {
            let applicableRate = 0;
            let applicableIsMarkup = false;

            for (let i = activeQuantities.length - 1; i >= 0; i--) {
                if (quantity >= activeQuantities[i]) {
                    const discountKey = `quantity_${activeQuantities[i]}_discount`;
                    const parsed = parseDiscountOrMarkup(settings[discountKey]);
                    applicableRate = parsed.rate;
                    applicableIsMarkup = parsed.isMarkup;
                    break;
                }
            }

            return { rate: applicableRate, isMarkup: applicableIsMarkup };
        };

        const discountData = getDiscountData();
        const discountRate = discountData.rate;
        const isMarkup = discountData.isMarkup;
        const totalPrice = basePrice * quantity;
        const discountedTotal = discountRate > 0
            ? (isMarkup ? totalPrice * (1 + discountRate / 100) : totalPrice * (1 - discountRate / 100))
            : totalPrice;
        const finalPrice = discountedTotal / quantity;

        return {
            total: Number(discountedTotal.toFixed(4)),
            perPiece: Number(finalPrice.toFixed(4)),
            discountRate: discountRate,
            isMarkup: isMarkup,
        };
    }, [labelWidth, labelHeight, applicationMethod, quantity, labelType, extras, fabricType, selectedQuality, activeQuantities]);

    const captureLabel = useCallback(async () => {
        try {
            // Wait for fonts to be ready
            await document.fonts.ready;

            const { widthScale } = calculateScaleFactor(labelWidth, labelHeight);
            const W = Math.round(labelWidth * widthScale);
            const H = Math.round(labelHeight * widthScale);
            const TARGET_PX = 2400;
            const dpr = Math.max(3, Math.min(6, Math.round(TARGET_PX / Math.max(W, H, 1))));

            const canvas = document.createElement('canvas');
            canvas.width = Math.round(W * dpr);
            canvas.height = Math.round(H * dpr);
            const ctx = canvas.getContext('2d');
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            ctx.scale(dpr, dpr);

            // ── Background ───────────────────────────────────────────────
            ctx.fillStyle = bgColor || '#ffffff';
            ctx.fillRect(0, 0, W, H);

            // ── Frame geometry ────────────────────────────────────────────
            const borderWidth = Math.max(1.5, Math.min(labelWidth, labelHeight, fontSize) * 0.02) + 1;
            const outerPad = Math.max(Math.min(labelWidth, labelHeight) * 0.15, 15);
            const innerPad = Math.max(Math.min(labelWidth, labelHeight) * 0.25, 25);

            if (frameStyle === 'single' || frameStyle === 'double') {
                ctx.strokeStyle = textColor;
                ctx.lineWidth = borderWidth;
                const op = outerPad + borderWidth / 2;
                ctx.strokeRect(op, op, W - op * 2, H - op * 2);
                if (frameStyle === 'double') {
                    const ip = innerPad + borderWidth / 2;
                    ctx.strokeRect(ip, ip, W - ip * 2, H - ip * 2);
                }
            }

            // ── Icon loading from SVG sprite ──────────────────────────────
            const hasLeftIcon = (iconPosition === 'left' || iconPosition === 'both') && icon && icon !== 'none';
            const hasRightIcon = (iconPosition === 'right' || iconPosition === 'both') && icon && icon !== 'none';
            const iconSize = fontSize;
            const iconGap = Math.max(2, Math.round(fontSize * 0.15));

            let iconImg = null;
            if ((hasLeftIcon || hasRightIcon) && icon !== 'none') {
                try {
                    // Sprite is injected as <svg style="display:none"> directly in footer
                    const sprite = document.querySelector('svg[style*="display:none"]') ||
                        document.querySelector('div[style*="display:none"] svg');
                    if (sprite) {
                        const symbol = sprite.querySelector(`symbol[id="${icon}"]`) ||
                            sprite.querySelector(`#${icon}`);
                        if (symbol) {
                            const vb = symbol.getAttribute('viewBox') || '0 0 16 16';
                            // Replace 'currentColor' with the actual text color so canvas renders it correctly
                            const iconContent = symbol.innerHTML.replace(/currentColor/g, textColor);
                            const svgStr = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}" width="${iconSize}" height="${iconSize}">${iconContent}</svg>`;
                            // Use base64 data URL (NOT blob URL) to prevent canvas taint on Safari/iOS
                            const svgBase64 = btoa(unescape(encodeURIComponent(svgStr)));
                            const dataUrl = `data:image/svg+xml;base64,${svgBase64}`;
                            iconImg = await new Promise((res) => {
                                const img = new Image();
                                img.onload = () => res(img);
                                img.onerror = () => { console.warn('CLD: icon img failed to load'); res(null); };
                                img.src = dataUrl;
                                setTimeout(() => res(null), 2000);
                            });
                        }
                    }
                } catch (_) { iconImg = null; }
            }


            // ── Layout ───────────────────────────────────────────────────
            const [vertical, horizontal] = (textAlignment || 'middle-center').split('-');
            const contentPad = frameStyle === 'double' ? innerPad
                : frameStyle === 'single' ? outerPad
                    : 0;
            const padH = contentPad + 12;
            const padV = contentPad + 8;

            const iconSlot = iconImg ? (iconSize + iconGap) : 0;
            const totalIconW = (hasLeftIcon ? iconSlot : 0) + (hasRightIcon ? iconSlot : 0);
            const textAreaW = Math.max(1, W - padH * 2 - totalIconW);
            const textAreaX = padH + (hasLeftIcon ? iconSlot : 0);
            const availH = H - padV * 2;

            // ── Text rendering ────────────────────────────────────────────
            ctx.font = `normal ${fontSize}px "${font}", Arial, sans-serif`;
            ctx.fillStyle = textColor;

            const lineHeight = Math.round(fontSize * 1.2);
            const rawLines = (text || '').split('\n');
            const wrappedLines = [];
            for (const para of rawLines) {
                const words = para.split(' ');
                let line = '';
                for (const word of words) {
                    const test = line ? line + ' ' + word : word;
                    if (ctx.measureText(test).width > textAreaW && line) {
                        wrappedLines.push(line);
                        line = word;
                    } else {
                        line = test;
                    }
                }
                wrappedLines.push(line || '');
            }
            const totalTextH = wrappedLines.length * lineHeight;

            let startY;
            if (vertical === 'top') {
                startY = padV + fontSize * 0.8;
            } else if (vertical === 'bottom') {
                startY = padV + availH - totalTextH + fontSize * 0.8;
            } else {
                startY = padV + (availH - totalTextH) / 2 + fontSize * 0.8;
            }

            let hAlign = 'center';
            let baseX = textAreaX + textAreaW / 2;
            if (horizontal === 'left') { hAlign = 'left'; baseX = textAreaX; }
            if (horizontal === 'right') { hAlign = 'right'; baseX = textAreaX + textAreaW; }

            ctx.textAlign = hAlign;
            ctx.textBaseline = 'alphabetic';
            for (let i = 0; i < wrappedLines.length; i++) {
                ctx.fillText(wrappedLines[i], baseX, startY + i * lineHeight, textAreaW);
            }

            // ── Icons ────────────────────────────────────────────────────
            if (iconImg) {
                const iconY = padV + (availH - iconSize) / 2;
                if (hasLeftIcon) ctx.drawImage(iconImg, padH, iconY, iconSize, iconSize);
                if (hasRightIcon) ctx.drawImage(iconImg, W - padH - iconSize, iconY, iconSize, iconSize);
            }

            return canvas.toDataURL('image/png', 1.0);

        } catch (e) {
            console.error('captureLabel error:', e);
            throw e;
        }
    }, [bgColor, frameStyle, textColor, font, fontSize, textAlignment, labelWidth, labelHeight, calculateScaleFactor, text, icon, iconPosition]);

    const addToCart = useCallback(async () => {
        if (loading || !text.trim()) {
            alert('Please add label text');
            return;
        }
        setLoading(true);
        // Yield thread control back to the browser to paint React's global loading portal blocker spinner
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
            const labelImage = await captureLabel();
            if (!labelImage) {
                throw new Error('Failed to create label image');
            }

            const compressedImage = await compressImage(labelImage);

            const largerSideMm = Math.max(labelWidth, labelHeight, 1);
            const exportDpi = Math.max(300, Math.round((2400 / largerSideMm) * 25.4));
            let renderAttachmentId = 0;
            try {
                const data = {
                    action: 'cld_store_design_image',
                    security: window.cld_ajax_obj.nonce,
                    image: compressedImage,
                    width_mm: String(labelWidth),
                    height_mm: String(labelHeight),
                    dpi: String(exportDpi)
                };
                const st = await axios({
                    method: 'POST',
                    url: window.cld_ajax_obj.rest_store_image || window.cld_ajax_obj.ajax_url,
                    data: new URLSearchParams(data),
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                });
                if (st.data && st.data.success) {
                    renderAttachmentId = Number(st.data.data.id) || 0;
                }
            } catch (e) {
                console.warn('CLD: cld_store_design_image failed', e);
            }

            // Fiyat hesaplama
            const priceInfo = calculatePrice();
            const designData = {
                text,
                font,
                fontSize: Math.round(fontSize),
                textColor,
                bgColor,
                icon,
                iconPosition,
                labelWidth: Number(labelWidth), // Sayısal türde gönder
                labelHeight: Number(labelHeight), // Sayısal türde gönder
                applicationMethod,
                frameStyle,
                labelType,
                fabricType: labelType === 'printed' ? fabricType : null,
                extras: labelType === 'woven' ? extras : null,
                textAlignment,
                selectedQuality: selectedQuality || 'standard', // Varsayılan değer
                imageData: renderAttachmentId ? '' : compressedImage,
                render_attachment_id: renderAttachmentId,
                created_at: new Date().toISOString(),
                created_by: 'labelgo',
                calculated_price: {
                    per_piece: Number(priceInfo.perPiece.toFixed(4)), // Yuvarlanmış değer
                    total: Number(priceInfo.total.toFixed(4)), // Yuvarlanmış değer
                },
            };

            // Nonce ve ajax_url kontrolü
            if (!window.cld_ajax_obj || !window.cld_ajax_obj.nonce || !window.cld_ajax_obj.ajax_url) {
                throw new Error('Sunucu yapılandırması eksik, lütfen sayfayı yenileyin.');
            }

            // Choose the correct WooCommerce product based on labelType (woven/printed)
            const root = document.getElementById('cld-label-designer-root');
            const legacyId = Number(root?.dataset?.productId || 0);
            const wovenId = Number(root?.dataset?.wovenProductId || 0);
            const printedId = Number(root?.dataset?.printedProductId || 0);
            const chosenId = labelType === 'woven' ? (wovenId || legacyId) : (printedId || legacyId);
            if (!chosenId) {
                throw new Error('No product configured for selected label type.');
            }

            const data = {
                action: 'add_to_cart_custom_label',
                product_id: chosenId,
                quantity: Number(quantity), // Sayısal türde gönder
                label_design: JSON.stringify(designData),
                custom_price: Number(priceInfo.perPiece.toFixed(4)), // Yuvarlanmış değer
                security: window.cld_ajax_obj.nonce,
            };

            const response = await axios({
                method: 'POST',
                url: window.cld_ajax_obj.rest_add_to_cart || window.cld_ajax_obj.ajax_url,
                data: new URLSearchParams(data),
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            });

            if (response.data.success) {
                window.location.href = response.data.data.cart_url;
            } else {
                throw new Error(response.data.data?.message || 'Error adding to cart');
            }
        } catch (error) {
            console.error('CLD: Add to cart error:', error);
            alert(error.message || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [applicationMethod, captureLabel, calculatePrice, extras, fabricType, icon, iconPosition,
        labelHeight, labelType, labelWidth, loading, quantity, text, bgColor, frameStyle,
        textColor, font, fontSize, textAlignment, selectedQuality]);

    // Helper to determine if a hex color is dark or light
    const isDarkColor = useCallback((hex) => {
        if (!hex) return false;
        const color = hex.replace('#', '');
        const r = parseInt(color.substring(0, 2), 16);
        const g = parseInt(color.substring(2, 4), 16);
        const b = parseInt(color.substring(4, 6), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) return false;
        const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
        return brightness < 140; // 140 is a great middle ground for dark colors
    }, []);

    // Resolve texture image URL for woven qualities and background color brightness
    const resolveTextureImg = useCallback((quality, bg) => {
        const base = (window.cld_ajax_obj && window.cld_ajax_obj.plugin_url)
            ? window.cld_ajax_obj.plugin_url
            : `${window.location.origin}/wp-content/plugins/label-studio-4.2.2/`;
        const q = quality || selectedQuality || 'standard';
        const isDark = isDarkColor(bg || bgColor);

        let name = 'texture.png'; // standard default
        if (q === 'eco') {
            name = isDark ? 'ecotextureblack.png' : 'ecotexture.png';
        } else if (q === 'premium') {
            name = isDark ? 'premiumtextureblack.png' : 'premiumtexture.png';
        } else {
            name = isDark ? 'textureblack.png' : 'texture.png';
        }
        return `${base}assets/texture/${name}`;
    }, [selectedQuality, bgColor, isDarkColor]);

    const renderLabelContent = useCallback(() => {
        const content = text || 'Enter label text';
        const containerClassName = `label-container ${frameStyle !== 'none' ? `with-${frameStyle}-frame` : ''}`;
        const textureClass = labelType === 'printed' ? fabricType : labelType === 'woven' ? `woven-${selectedQuality}` : '';

        const config = {
            none: {
                padding: {
                    v: Math.max(4, labelHeight * 0.1),
                    h: Math.max(4, labelWidth * 0.1)
                }
            },
            single: {
                borderWidth: Math.max(1.5, Math.min(labelWidth, labelHeight, fontSize) * 0.02) + 1,
                outerPadding: Math.max(Math.min(labelWidth, labelHeight) * 0.15, 15),
                padding: 2
            },
            double: {
                borderWidth: Math.max(1.5, Math.min(labelWidth, labelHeight, fontSize) * 0.02) + 1,
                outerPadding: Math.max(Math.min(labelWidth, labelHeight) * 0.15, 15),
                innerPadding: Math.max(Math.min(labelWidth, labelHeight) * 0.25, 25),
                padding: 2
            }
        };

        const currentConfig = config[frameStyle];
        const iconSize = fontSize;
        const iconGap = Math.max(2, Math.round(fontSize * 0.15));
        const [vertical, horizontal] = textAlignment.split('-') || ['middle', 'center'];

        // Icon elementi sadece icon ve iconPosition 'none' değilse gösterilecek
        const shouldShowIcon = icon !== 'none' && iconPosition !== 'none';
        const iconEl = shouldShowIcon ? (
            <span
                className="icon"
                style={{
                    width: `${iconSize}px`,
                    height: `${iconSize}px`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: '0 0 auto',
                    lineHeight: 1,
                    verticalAlign: 'middle',
                }}
            >
                <Icon name={icon} color={textColor} />
            </span>
        ) : null;

        return (
            <div className={containerClassName}>
                {/* Frame elements */}
                {frameStyle !== 'none' && (
                    <div
                        className="outer-frame"
                        style={{
                            border: `${currentConfig.borderWidth}px solid ${textColor}`,
                            top: `${currentConfig.outerPadding}px`,
                            left: `${currentConfig.outerPadding}px`,
                            right: `${currentConfig.outerPadding}px`,
                            bottom: `${currentConfig.outerPadding}px`,
                            position: 'absolute',
                            boxSizing: 'border-box',
                        }}
                    />
                )}
                {frameStyle === 'double' && (
                    <div
                        className="inner-frame"
                        style={{
                            border: `${currentConfig.borderWidth}px solid ${textColor}`,
                            top: `${currentConfig.innerPadding}px`,
                            left: `${currentConfig.innerPadding}px`,
                            right: `${currentConfig.innerPadding}px`,
                            bottom: `${currentConfig.innerPadding}px`,
                            position: 'absolute',
                            boxSizing: 'border-box',
                        }}
                    />
                )}
                <div
                    className={`label-content-wrapper ${frameStyle !== 'none' ? `${frameStyle}-frame` : ''} ${textureClass}`}
                    style={{
                        background: bgColor,
                        color: textColor,
                        fontFamily: font,
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        position: 'relative',
                    }}
                >
                    {labelType === 'woven' && (
                        <div
                            className="texture-overlay"
                            style={{
                                backgroundImage: `url(${resolveTextureImg(selectedQuality, bgColor)})`
                            }}
                        />
                    )}
                    <div
                        className={`label-text-display align-${textAlignment}`}
                        style={{
                            fontSize: `${fontSize}px`,
                            display: 'flex',
                            padding: frameStyle === 'none' ? '8px 12px' : 0,
                            margin: '0',
                            lineHeight: 1.2,
                            boxSizing: 'border-box',
                            width: `calc(100% - ${frameStyle === 'double' ? (currentConfig.innerPadding * 2) : frameStyle === 'single' ? (currentConfig.outerPadding * 2) : 0}px)`,
                            height: `calc(100% - ${frameStyle === 'double' ? (currentConfig.innerPadding * 2) : frameStyle === 'single' ? (currentConfig.outerPadding * 2) : 0}px)`,
                            alignItems: 'stretch',
                            justifyContent: vertical === 'middle' ? 'center' : vertical === 'top' ? 'flex-start' : 'flex-end',
                            position: 'absolute',
                            left: frameStyle === 'double' ? currentConfig.innerPadding : frameStyle === 'single' ? currentConfig.outerPadding : 0,
                            top: frameStyle === 'double' ? currentConfig.innerPadding : frameStyle === 'single' ? currentConfig.outerPadding : 0,
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: horizontal === 'center' ? 'center' : horizontal === 'left' ? 'flex-start' : 'flex-end',
                                gap: shouldShowIcon ? `${iconGap}px` : 0,
                                width: '100%',
                                height: '100%',
                                flexWrap: 'nowrap',
                                boxSizing: 'border-box',
                                padding: `0 ${frameStyle !== 'none' ? '8px' : '4px'}`,
                                ...(shouldShowIcon && iconPosition === 'left' && {
                                    paddingLeft: frameStyle !== 'none' ? '8px' : '4px'
                                }),
                                ...(shouldShowIcon && iconPosition === 'right' && {
                                    paddingRight: frameStyle !== 'none' ? '8px' : '4px'
                                }),
                                ...(shouldShowIcon && iconPosition === 'both' && {
                                    padding: `0 ${frameStyle !== 'none' ? '8px' : '4px'}`
                                })
                            }}
                        >
                            {shouldShowIcon && (iconPosition === 'left' || iconPosition === 'both') && iconEl}
                            <div
                                style={{
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                    maxWidth: shouldShowIcon ?
                                        `calc(100% - ${iconSize * (iconPosition === 'both' ? 2 : 1) + iconGap * (iconPosition === 'both' ? 2 : 1)}px)` :
                                        '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: vertical === 'middle' ? 'center' : vertical === 'top' ? 'flex-start' : 'flex-end',
                                    justifyContent: horizontal === 'center' ? 'center' : horizontal === 'left' ? 'flex-start' : 'flex-end',
                                    padding: `0 ${frameStyle !== 'none' ? '2px' : '4px'}`,
                                }}
                            >
                                <span
                                    className="label-content"
                                    style={{
                                        wordBreak: 'break-word',
                                        overflowWrap: 'break-word',
                                        textAlign: horizontal,
                                        whiteSpace: 'normal',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {content}
                                </span>
                            </div>
                            {shouldShowIcon && (iconPosition === 'right' || iconPosition === 'both') && iconEl}
                        </div>
                    </div>
                </div>
                <div className="size-indicator">
                    <div className="width-indicator">{`${labelWidth}mm`}</div>
                    <div className="height-indicator">{`${labelHeight}mm`}</div>
                    <div className="dimension-line width-line" />
                    <div className="dimension-line height-line" />
                </div>
                {applicationMethod !== 'iron-on' && (
                    <div className="seam-allowance-indicators">
                        <div className="seam-allowance left">
                            <div className="seam-fold-line" />
                            <div className="seam-measurement">8mm</div>
                        </div>
                        <div className="seam-allowance right">
                            <div className="seam-fold-line" />
                            <div className="seam-measurement">8mm</div>
                        </div>
                    </div>
                )}
            </div>
        );
    }, [text, bgColor, icon, frameStyle, textColor, font, fontSize, iconPosition, labelType, fabricType, selectedQuality, textAlignment, labelWidth, labelHeight, applicationMethod, resolveTextureImg]);

    useEffect(() => {
        // Sadece icon değiştiğinde çalışsın
        if (icon === 'none') {
            // Icon none olduğunda position'ı da none yap
            setIconPosition('none');
        } else if (iconPosition === 'none') {
            // Icon varsa ve position none ise left'e çek
            setIconPosition('left');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [icon]);

    useEffect(() => {
        if (labelType === 'printed') {
            setBgColor(fabricType === 'organic-cotton' ? '#F5F5DC' : '#FFFFFF');
            setApplicationMethod('sew-on');
        } else if (labelType === 'woven') {
            setBgColor('#FFFFFF');
        }
    }, [labelType, fabricType]);

    useEffect(() => {
        if (labelRef.current) {
            const wrapper = labelRef.current.querySelector('.label-content-wrapper');
            if (wrapper) {
                wrapper.style.background = bgColor;
            }
        }
    }, [bgColor]);

    const prices = useMemo(() => calculatePrice(), [calculatePrice]);

    const calculateContrastRatio = (color1, color2) => {
        const hexToRgb = (hex) => {
            const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
            hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result
                ? {
                    r: parseInt(result[1], 16),
                    g: parseInt(result[2], 16),
                    b: parseInt(result[3], 16),
                }
                : { r: 0, g: 0, b: 0 };
        };

        // Renklerin RGB değerlerini al
        const rgb1 = hexToRgb(color1);
        const rgb2 = hexToRgb(color2);

        // Renk farklılığını hesapla (Delta E)
        const calculateDeltaE = (rgb1, rgb2) => {
            // RGB'den LAB renk uzayına dönüşüm için yardımcı fonksiyonlar
            const rgbToXyz = (r, g, b) => {
                r = r / 255;
                g = g / 255;
                b = b / 255;

                r = r > 0.04045 ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92;
                g = g > 0.04045 ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92;
                b = b > 0.04045 ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92;

                r = r * 100;
                g = g * 100;
                b = b * 100;

                const x = r * 0.4124 + g * 0.3576 + b * 0.1805;
                const y = r * 0.2126 + g * 0.7152 + b * 0.0722;
                const z = r * 0.0193 + g * 0.1192 + b * 0.9505;

                return [x, y, z];
            };

            const xyzToLab = (x, y, z) => {
                x = x / 95.047;
                y = y / 100;
                z = z / 108.883;

                x = x > 0.008856 ? Math.pow(x, 1 / 3) : (7.787 * x) + (16 / 116);
                y = y > 0.008856 ? Math.pow(y, 1 / 3) : (7.787 * y) + (16 / 116);
                z = z > 0.008856 ? Math.pow(z, 1 / 3) : (7.787 * z) + (16 / 116);

                const l = (116 * y) - 16;
                const a = 500 * (x - y);
                const b = 200 * (y - z);

                return [l, a, b];
            };

            // RGB'den LAB'a dönüşüm
            const [x1, y1, z1] = rgbToXyz(rgb1.r, rgb1.g, rgb1.b);
            const [x2, y2, z2] = rgbToXyz(rgb2.r, rgb2.g, rgb2.b);

            const [l1, a1, b1] = xyzToLab(x1, y1, z1);
            const [l2, a2, b2] = xyzToLab(x2, y2, z2);

            // Delta E hesaplama (CIE76 formülü)
            return Math.sqrt(
                Math.pow(l2 - l1, 2) +
                Math.pow(a2 - a1, 2) +
                Math.pow(b2 - b1, 2)
            );
        };

        // Renk farklılığını hesapla
        const deltaE = calculateDeltaE(rgb1, rgb2);

        // Renk parlaklık farkını hesapla
        const getBrightness = (rgb) => {
            return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
        };

        const brightness1 = getBrightness(rgb1);
        const brightness2 = getBrightness(rgb2);
        const brightnessDiff = Math.abs(brightness1 - brightness2);

        // Sonuç objesi
        return {
            deltaE: deltaE,
            brightnessDiff: brightnessDiff,
            isSimilar: deltaE < 30, // Renklerin benzerliği için eşik değeri
            isLowContrast: brightnessDiff < 50, // Düşük kontrast için eşik değeri
            needsWarning: deltaE < 30 && brightnessDiff < 50 // Her iki koşul da sağlanıyorsa uyarı gerekir
        };
    };

    const checkColors = (newTextColor, newBgColor, setColorCallback, colorToSet) => {
        if (newTextColor === newBgColor) {
            setOverlay({
                show: true,
                message: t('same_color_warning', 'Text color and background color cannot be the same!'),
                onConfirm: null,
            });
            return false;
        }

        const contrast = calculateContrastRatio(newTextColor, newBgColor);

        if (contrast.needsWarning) {
            setOverlay({
                show: true,
                message: t('low_contrast_warning', 'Warning: The selected colors are too similar and may be hard to read. Do you want to proceed anyway?'),
                onConfirm: () => setColorCallback(colorToSet),
            });
            return false;
        }

        return true;
    };

    // Label type değiştiğinde quality seçimini sıfırla
    useEffect(() => {
        setSelectedQuality('standard');
    }, [labelType]);

    const isValid = !!text.trim();

    return (
        <div className="label-designer-wrapper">
            <div className="orientation-warning-overlay">
                <span>{t('please_rotate_your_device_to_portrait', 'Please rotate your device to portrait')}</span>
            </div>
            <div className="designer-container">
                {/* Mobil Kategori Sekmeleri */}
                <div className="mobile-category-tabs">
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'type_size' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('type_size'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('Type & Size', 'Type & Size')}
                    </button>
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'text_font' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('text_font'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('Text & Font', 'Text & Font')}
                    </button>
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'icon_frame' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('icon_frame'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('Icon & Frame', 'Icon & Frame')}
                    </button>
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'colors' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('colors'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('Colors', 'Colors')}
                    </button>
                    <button
                        type="button"
                        className={`mobile-tab-btn ${activeMobileTab === 'application_extras' ? 'active' : ''}`}
                        onClick={(e) => { setActiveMobileTab('application_extras'); e.target.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" }); }}
                    >
                        {t('Application & Extras', 'Application & Extras')}
                    </button>
                </div>

                <div className="designer-controls" data-active-mobile-tab={activeMobileTab}>

                    <div className="control-group tab-type_size">
                        <div className="group-title">
                            {t('label_type', 'Label Type')}
                            <span className="help-icon">?
                                <span className="tooltip">
                                    {t('label_type_tooltip', 'Choose between woven or printed labels. Woven labels are more durable and premium, while printed labels are more economical.')}
                                </span>
                            </span>
                        </div>
                        <div className="label-type-buttons">
                            {[
                                { value: 'woven', preview: t('woven_label', 'WOVEN') },
                                { value: 'printed', preview: t('printed_label', 'PRINTED') }
                            ].map(type => (
                                <button
                                    key={type.value}
                                    className={`label-type-button ${labelType === type.value ? 'selected' : ''}`}
                                    onClick={() => setLabelType(type.value)}
                                >
                                    <div className={`type-preview ${type.value}-preview`}>
                                        <div className="preview-border">
                                            <div className="preview-content">
                                                <span>{type.preview}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <span className="type-label">{t(type.value, type.value === 'woven' ? 'Woven' : 'Printed')}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {labelType === 'printed' && (
                        <div className="control-group tab-type_size">
                            <div className="group-title">{t('material_type', 'Fabric Type')}</div>
                            <div className="fabric-type-buttons">
                                {[
                                    { value: 'satin', title: t('satin', 'Satin'), desc: t('smooth_finish', 'Smooth finish') },
                                    { value: 'edge-satin', title: t('edge-satin', 'Edge Satin'), desc: t('knitted_edges', 'Knitted edges') },
                                    { value: 'white-cotton', title: t('white-cotton', 'White Cotton'), desc: t('natural_feel', 'Natural feel') },
                                    { value: 'organic-cotton', title: t('organic-cotton', 'Organic Cotton'), desc: t('eco_friendly', 'Eco-friendly') }
                                ].map(fabric => (
                                    <button
                                        key={fabric.value}
                                        className={`fabric-type-button ${fabric.value} ${fabricType === fabric.value ? 'selected' : ''}`}
                                        onClick={() => setFabricType(fabric.value)}
                                    >
                                        <div className="fabric-icon"></div>
                                        <div className="fabric-info">
                                            <span className="fabric-title">{fabric.title}</span>
                                            <span className="fabric-desc">{fabric.desc}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {labelType === 'woven' && (
                        <div className="control-group tab-type_size">
                            <div className="group-title">
                                {t('woven_quality', 'Quality')}
                                <span className="help-icon">?
                                    <span className="tooltip">
                                        {t('woven_quality_tooltip', 'Select weave density. Eco: 52 Density, Standard: 60 Density, Premium: auto-adjusts to the highest possible density (approx. 70–80) based on your graphic.')}
                                    </span>
                                </span>
                            </div>
                            <div className="quality-options">
                                {[
                                    { value: 'eco', title: t('eco', 'Eco'), desc: t('eco_density', '52 Density') },
                                    { value: 'standard', title: t('standard', 'Standard'), desc: t('standard_density', '60 Density') },
                                    { value: 'premium', title: t('premium', 'Premium'), desc: t('premium_density', '70-80 Density') }
                                ].map(quality => (
                                    <button
                                        key={quality.value}
                                        className={`quality-button ${quality.value} ${selectedQuality === quality.value ? 'selected' : ''}`}
                                        onClick={() => setSelectedQuality(quality.value)}
                                    >
                                        <div className="quality-icon"></div>
                                        <div className="quality-info">
                                            <span className="quality-title">{quality.title}</span>
                                            <span className="quality-desc">{quality.desc}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="control-group tab-type_size">
                        <div className="group-title">
                            {t('size', 'Label Size')}
                            <span className="help-icon">?
                                <span className="tooltip">
                                    {t('dimensions_tooltip', 'Select the dimensions of your label. Standard sizes are optimized for different garment types and locations.')}
                                </span>
                            </span>
                        </div>
                        <div className="size-options">
                            {sizeOptions.map(size => (
                                <div
                                    key={size.name}
                                    className={`size-option ${labelWidth === size.width && labelHeight === size.height ? 'selected' : ''}`}
                                    onClick={() => { setLabelWidth(size.width); setLabelHeight(size.height); }}
                                >
                                    <div className="size-preview">
                                        <div className="size-box" style={{ width: `${size.width * 0.8}px`, height: `${size.height * 0.8}px` }} />
                                    </div>
                                    <div className="size-details">
                                        <div className="size-name">{size.name}</div>
                                        <div className="size-dimensions">{`${size.height}x${size.width}mm`}</div>
                                        <div className="size-info">{size.info}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="control-group tab-text_font">
                        <div className="group-title">
                            {t('label_text', 'Label Text')}
                            <span className="help-icon">?
                                <span className="tooltip">
                                    {t('label_text_tooltip', 'Enter the text that will appear on your label. You can adjust the font size and style to match your design needs.')}
                                </span>
                            </span>
                        </div>
                        <div className="text-control-wrapper">
                            <input
                                type="text"
                                value={text}
                                onChange={e => setText(e.target.value)}
                                className="label-text-input"
                                placeholder={t('enter_label_text', 'Enter label text')}
                                maxLength={70}
                            />
                            <div className="font-size-control">
                                <label>{t('font_size', 'Font Size')}: {Math.round(fontSize)}px</label>
                                <input
                                    type="range"
                                    min={getFontSizeRange().min}
                                    max={getFontSizeRange().max}
                                    value={fontSize}
                                    onChange={e => setFontSize(Number(e.target.value))}
                                    step="1"
                                    className="range-slider"
                                />
                            </div>
                            <div className="control-group tab-text_font">
                                <div className="group-title">
                                    {t('alignment', 'Text Alignment')}
                                    <span className="help-icon">?
                                        <span className="tooltip">
                                            {t('text_alignment_tooltip', 'Control how your text is positioned on the label. Choose from 9 different alignment options for perfect placement.')}
                                        </span>
                                    </span>
                                </div>
                                <div className="text-alignment-grid">
                                    {alignmentOptions.map(align => (
                                        <button
                                            key={align.value}
                                            className={`alignment-button ${textAlignment === align.value ? 'selected' : ''}`}
                                            onClick={() => setTextAlignment(align.value)}
                                        >
                                            <div className="alignment-preview">
                                                <div className={`alignment-bar ${align.value}`}>
                                                    <span className="alignment-text">{align.preview}</span>
                                                </div>
                                            </div>
                                            <span className="alignment-label">{align.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="control-group tab-text_font">
                        <div className="group-title">
                            {t('font_style', 'Font Style')}
                            <span className="help-icon">?
                                <span className="tooltip">
                                    {t('font_style_tooltip', 'Choose from a variety of fonts to customize your label\'s appearance.')}
                                </span>
                            </span>
                        </div>
                        <div className="font-category-selector">
                            <button
                                className="scroll-button left"
                                onClick={() => scrollFontCategories('left')}
                                disabled={fontScrollPosition <= 0}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15 18l-6-6 6-6" />
                                </svg>
                            </button>
                            <div className="font-category-list" ref={fontCategorySliderRef} onScroll={handleFontScroll}>
                                {Object.entries(fontCategories).map(([category, fonts]) => (
                                    <button
                                        key={category}
                                        className={`font-category-button ${fontCategory === category ? 'active' : ''}`}
                                        onClick={() => setFontCategory(category)}
                                    >
                                        <span
                                            className="font-preview-text"
                                            style={{ fontFamily: fonts[0] }}
                                        >
                                            Aa
                                        </span>
                                        <span className="font-category-name">{t(category, category)}</span>
                                    </button>
                                ))}
                            </div>
                            <button
                                className="scroll-button right"
                                onClick={() => scrollFontCategories('right')}
                                disabled={fontScrollPosition >= (fontCategorySliderRef.current?.scrollWidth - fontCategorySliderRef.current?.clientWidth - 5)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>
                        </div>
                        <div className="font-options-grid">
                            {fontCategories[fontCategory].map(fontName => (
                                <button
                                    key={fontName}
                                    className={`font-option ${font === fontName ? 'selected' : ''}`}
                                    style={{ fontFamily: fontName }}
                                    onClick={() => setFont(fontName)}
                                >
                                    <span className="font-preview">Aa</span>
                                    <span className="font-name">{fontName}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="control-group tab-icon_frame">
                        <div className="group-title">
                            {t('icon_and_position', 'Icon & Position')}
                            <span className="help-icon">?
                                <span className="tooltip">
                                    {t('icon_and_position_tooltip', 'Add decorative icons to enhance your label design. Icons can be placed on either side or both sides of the text.')}
                                </span>
                            </span>
                        </div>
                        <div className="icon-position-options">
                            {iconPositions.map(pos => (
                                <button
                                    key={pos.value}
                                    className={`position-button ${iconPosition === pos.value ? 'selected' : ''}`}
                                    onClick={() => setIconPosition(pos.value)}
                                >
                                    <div className="position-preview">
                                        <div className="preview-frame">
                                            <span className="preview-text">{pos.preview}</span>
                                        </div>
                                    </div>
                                    <span className="position-label">{pos.label}</span>
                                </button>
                            ))}
                        </div>
                        <div className="icon-category-section">
                            <button
                                className="scroll-button left"
                                onClick={() => scrollCategories('left')}
                                disabled={scrollPosition <= 0}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M15 18l-6-6 6-6" />
                                </svg>
                            </button>
                            <div className="icon-category-slider" ref={categorySliderRef}>
                                {iconCategories.map(category => (
                                    <button
                                        key={category.name}
                                        className={`icon-category-button ${iconCategory === category.name ? 'active' : ''}`}
                                        onClick={() => setIconCategory(category.name)}
                                    >
                                        <span className="category-icon">
                                            <Icon name={category.icon} color="#000000" />
                                        </span>
                                        <span className="category-name">{t(category.name, category.name)}</span>
                                    </button>
                                ))}
                            </div>
                            <button
                                className="scroll-button right"
                                onClick={() => scrollCategories('right')}
                                disabled={scrollPosition >= (categorySliderRef.current?.scrollWidth - categorySliderRef.current?.clientWidth)}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M9 18l6-6-6-6" />
                                </svg>
                            </button>
                        </div>
                        <div className="icon-options-grid">
                            {displayedIcons.map((iconName) => (
                                <button
                                    key={iconName}
                                    className={`icon-option ${icon === iconName ? 'selected' : ''}`}
                                    onClick={() => setIcon(iconName)}
                                >
                                    <span className="icon-preview">
                                        <Icon name={iconName} color={textColor} size={24} />
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="control-group tab-icon_frame">
                        <div className="group-title">
                            {t('frame', 'Frame Style')}
                            <span className="help-icon">?
                                <span className="tooltip">
                                    {t('frame_style_tooltip', 'Add decorative frames around your text. Choose from single, double, or no frame to suit your design style.')}
                                </span>
                            </span>
                        </div>
                        <div className="frame-style-buttons">
                            {['none', 'single', 'double'].map(style => (
                                <button
                                    key={style}
                                    className={`frame-button ${frameStyle === style ? 'selected' : ''}`}
                                    onClick={() => setFrameStyle(style)}
                                >
                                    <div className={`frame-preview ${style}-frame`}>
                                        <div className="preview-content"></div>
                                    </div>
                                    <span>{style === 'none' ? t('no_frame', 'No Frame') : (style === 'single' ? t('single_frame', 'Single Frame') : t('double_frame', 'Double Frame'))}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="control-group tab-colors">
                        <div className="group-title">
                            {t('colors', 'Colors')}
                            <span className="help-icon">?
                                <span className="tooltip">
                                    {t('colors_tooltip', 'Select background and text colors for your label. For optimal readability, ensure sufficient contrast between background and text colors. Avoid using the same color for both. Our system will help you select compatible color combinations.')}
                                </span>
                            </span>
                        </div>
                        <div className="color-options-wrapper">
                            {labelType === 'woven' && (
                                <div className="color-section">
                                    <label>{t('background_color', 'Background Color')}</label>
                                    <div className="color-swatch-row">
                                        <button
                                            type="button"
                                            className="color-swatch-button"
                                            style={{ backgroundColor: bgColor, border: bgColor === '#FFFFFF' ? '1px solid #E5E7EB' : 'none' }}
                                            onClick={() => setOpenColorPicker(prev => prev === 'bg' ? null : 'bg')}
                                        />
                                        {openColorPicker === 'bg' && (
                                            <div className="color-popover">
                                                <div className="color-popover-header">
                                                    {LGC_CATEGORIES.map(cat => (
                                                        <button
                                                            key={cat}
                                                            type="button"
                                                            className={`category-pill ${activeColorCategory === cat ? 'active' : ''}`}
                                                            onClick={() => setActiveColorCategory(cat)}
                                                        >
                                                            {t(cat, cat)}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="color-popover-grid">
                                                    {(LGC_COLORS[activeColorCategory] || []).map(opt => (
                                                        <div key={opt.code} className="color-chip-wrap">
                                                            <button
                                                                type="button"
                                                                className="color-chip"
                                                                title={`${opt.name} (${opt.code})`}
                                                                style={{ backgroundColor: opt.hex }}
                                                                onClick={() => {
                                                                    if (checkColors(textColor, opt.hex, setBgColor, opt.hex)) {
                                                                        setBgColor(opt.hex);
                                                                    }
                                                                    setOpenColorPicker(null);
                                                                }}
                                                            />
                                                            <div className="color-chip-meta">
                                                                <span className="chip-code">{opt.code}</span>
                                                                <span className="chip-name">{opt.name}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {(() => {
                                        const info = getLgcInfoByHex(bgColor);
                                        return info ? (
                                            <div className="color-chip-meta" style={{ marginTop: 6 }}>
                                                <span className="chip-code">{info.code}</span>
                                                <span className="chip-name">{info.name}</span>
                                            </div>
                                        ) : null;
                                    })()}
                                </div>
                            )}
                            <div className="color-section">
                                <label>{t('text_color', 'Text Color')}</label>
                                <div className="color-swatch-row">
                                    <button
                                        type="button"
                                        className="color-swatch-button"
                                        style={{ backgroundColor: textColor, border: textColor === '#FFFFFF' ? '1px solid #E5E7EB' : 'none' }}
                                        onClick={() => setOpenColorPicker(prev => prev === 'text' ? null : 'text')}
                                    />
                                    {openColorPicker === 'text' && (
                                        <div className="color-popover">
                                            <div className="color-popover-header">
                                                {LGC_CATEGORIES.map(cat => (
                                                    <button
                                                        key={cat}
                                                        type="button"
                                                        className={`category-pill ${activeColorCategory === cat ? 'active' : ''}`}
                                                        onClick={() => setActiveColorCategory(cat)}
                                                    >
                                                        {t(cat, cat)}
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="color-popover-grid">
                                                {(LGC_COLORS[activeColorCategory] || []).map(opt => (
                                                    <div key={opt.code} className="color-chip-wrap">
                                                        <button
                                                            type="button"
                                                            className="color-chip"
                                                            title={`${opt.name} (${opt.code})`}
                                                            style={{ backgroundColor: opt.hex }}
                                                            onClick={() => {
                                                                if (checkColors(opt.hex, bgColor, setTextColor, opt.hex)) {
                                                                    setTextColor(opt.hex);
                                                                }
                                                                setOpenColorPicker(null);
                                                            }}
                                                        />
                                                        <div className="color-chip-meta">
                                                            <span className="chip-code">{opt.code}</span>
                                                            <span className="chip-name">{opt.name}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                {(() => {
                                    const info = getLgcInfoByHex(textColor);
                                    return info ? (
                                        <div className="color-chip-meta" style={{ marginTop: 6 }}>
                                            <span className="chip-code">{info.code}</span>
                                            <span className="chip-name">{info.name}</span>
                                        </div>
                                    ) : null;
                                })()}
                            </div>
                        </div>
                    </div>

                    {labelType === 'woven' && (
                        <>
                            <div className="control-group tab-application_extras">
                                <div className="group-title">
                                    {t('application_method', 'Application Method')}
                                    <span className="help-icon">?
                                        <span className="tooltip">
                                            {t('application_method_tooltip', 'Choose how the label will be attached: sew-on for traditional application or iron-on for heat application.')}
                                        </span>
                                    </span>
                                </div>
                                <div className="application-options">
                                    <button
                                        className={`application-button sew-on ${applicationMethod === 'sew-on' ? 'selected' : ''}`}
                                        onClick={() => setApplicationMethod('sew-on')}
                                    >
                                        <div className="method-icon">
                                            <svg
                                                viewBox="0 0 512 512"
                                                xmlns="http://www.w3.org/2000/svg"
                                                style={{
                                                    fill: 'currentColor',
                                                    fillOpacity: 1,
                                                    stroke: 'none'
                                                }}
                                            >
                                                <path d="M415.532,200.021h-33.433l-17.997-0.768c-0.397-0.017-0.793-0.025-1.19-0.025h-44.985 c-7.593,0-13.772-6.178-13.772-13.772c0-7.593,6.179-13.771,13.772-13.771h86.768c38.302,0,69.464-31.161,69.464-69.463 c0-38.303-31.161-69.464-69.464-69.464H258.508C249.299,12.964,229.315,0,207.174,0c-15.981,0-31.29,6.803-42.001,18.663 c-3.829,4.24-6.941,9.005-9.304,14.096H65.687c-15.354,0-27.846,12.491-27.846,27.846s12.492,27.846,27.846,27.846h87.853 l42.174,413.037c0.511,5.906,5.519,10.512,11.459,10.512c5.941,0,10.949-4.606,11.459-10.512l30.988-303.483 c5.926,32.331,34.29,56.915,68.307,56.915h44.391l17.998,0.768c0.397,0.017,0.793,0.025,1.19,0.025h34.027 c1.509,0,2.935,1.427,2.935,2.936c0,1.509-1.427,2.935-2.935,2.935h-90.063c-34.691,0-62.914,28.223-62.914,62.914 s28.223,62.914,62.914,62.914h37.905c15.354,0,27.846-12.491,27.846-27.846s-12.492-27.846-27.846-27.846h-37.905 c-3.983,0-7.222-3.24-7.222-7.222c0-3.982,3.239-7.221,7.222-7.221h90.063c32.328,0,58.628-26.301,58.628-58.628 C474.159,226.323,447.859,200.021,415.532,200.021z M65.687,73.262c-6.98,0-12.657-5.678-12.657-12.657 c0-6.98,5.678-12.657,12.657-12.657h85.573c-0.733,4.725-0.88,9.56-0.387,14.394l1.115,10.921H65.687z M207.174,464.192 L165.983,60.798c-1.188-11.632,2.626-23.279,10.462-31.956c7.836-8.678,19.037-13.654,30.729-13.654 c13.673,0,26.226,6.755,33.862,17.57h-33.109c-15.355,0-27.846,12.491-27.846,27.846s12.491,27.846,27.846,27.846h37.614 L207.174,464.192z M415.532,302.088h-90.063c-12.357,0-22.41,10.053-22.41,22.409c0,12.358,10.054,22.41,22.41,22.41h37.905 c6.98,0,12.657,5.678,12.657,12.657s-5.678,12.657-12.657,12.657h-37.905c-26.315,0-47.725-21.409-47.725-47.725 s21.41-47.725,47.725-47.725h90.063c9.824,0,18.124-8.3,18.124-18.124c0-9.825-8.3-18.124-18.124-18.124h-34.027 c-0.179,0-0.358-0.003-0.54-0.011l-18.162-0.775c-0.107-0.004-0.216-0.007-0.323-0.007h-44.554 c-29.928,0-54.275-24.348-54.275-54.275c0-29.927,24.348-54.274,54.275-54.274h86.768c15.969,0,28.961-12.991,28.961-28.96 c0-15.969-12.991-28.961-28.961-28.961H253.954c-0.007,0-0.015,0-0.022,0h-46.007c-6.98,0-12.657-5.678-12.657-12.657 s5.678-12.657,12.657-12.657h196.77c29.928,0,54.275,24.348,54.275,54.275c0,29.927-24.348,54.274-54.275,54.274h-86.768 c-15.969,0-28.961,12.991-28.961,28.96c0,15.969,12.991,28.961,28.961,28.961h44.985c0.179,0,0.358,0.003,0.54,0.011l18.162,0.775 c0.107,0.004,0.216,0.007,0.323,0.007h33.595c23.953,0,43.439,19.486,43.439,43.439S439.484,302.088,415.532,302.088z M253.566,159.37l7.241-70.919h143.889c7.593,0,13.772,6.178,13.772,13.772c0,7.593-6.179,13.771-13.772,13.771h-86.768 C288.85,115.994,263.903,133.962,253.566,159.37z" />
                                            </svg>
                                        </div>
                                        <div className="application-info">
                                            <span className="application-title">{t('sew_on', 'Sew-on')}</span>
                                            <span className="application-desc">{t('traditional_method', 'Traditional method')}</span>
                                        </div>
                                    </button>
                                    <button
                                        className={`application-button iron-on ${applicationMethod === 'iron-on' ? 'selected' : ''}`}
                                        onClick={() => setApplicationMethod('iron-on')}
                                    >
                                        <div className="method-icon">
                                            <svg fill="currentColor" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M55,24H16v2h39c2.2,0,4,1.8,4,4v7H33C13.7,37,3,45.2,3,60v1h58V30C61,26.7,58.3,24,55,24z M59,59H5c0.6-17.4,17.7-20,28-20 h26V59z" />
                                            </svg>
                                        </div>
                                        <div className="application-info">
                                            <span className="application-title">{t('iron_on', 'Iron-on')}</span>
                                            <span className="application-desc">{t('heat_application', 'Heat application')}</span>
                                        </div>
                                    </button>
                                </div>
                            </div>

                            <div className="control-group tab-application_extras">
                                <div className="group-title">
                                    {t('extra_options', 'Extra Options')}
                                    <span className="help-icon">?
                                        <span className="tooltip">
                                            {t('extra_options_tooltip', 'Additional options like double backing for durability or ultrasonic cutting for clean edges.')}
                                        </span>
                                    </span>
                                </div>
                                <div className="extras-options">
                                    <button
                                        className={`extra-option-button standard ${(!extras.zemindouble && !extras.ultrasonicCutting) ? 'selected' : ''}`}
                                        onClick={() => setExtras({
                                            standard: true,
                                            zemindouble: false,
                                            ultrasonicCutting: false
                                        })}
                                    >
                                        <div className="method-icon">
                                            <svg viewBox="0 0 24 24">
                                                <path d="M4 12 L9 17 L20 6" stroke="currentColor" strokeWidth="2" fill="none" />
                                            </svg>
                                        </div>
                                        <div className="extra-info">
                                            <span className="extra-title">{t('standard', 'Standard')}</span>
                                            <span className="extra-desc">{t('basic_finish', 'Basic finish')}</span>
                                        </div>
                                    </button>
                                    <button
                                        className={`extra-option-button double-back ${extras.zemindouble ? 'selected' : ''}`}
                                        onClick={() => {
                                            if (extras.zemindouble && !extras.ultrasonicCutting) {
                                                setExtras(prev => ({
                                                    ...prev,
                                                    standard: false,
                                                    zemindouble: true
                                                }));
                                                return;
                                            }
                                            setExtras(prev => ({
                                                ...prev,
                                                standard: false,
                                                zemindouble: !prev.zemindouble
                                            }));
                                        }}
                                    >
                                        <div className="method-icon">
                                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <polyline points="10 18 12 16 14 18" style={{ fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '1.5' }} />
                                                <path d="M18,11H6 M18,13H6 M12,16v5" style={{ fill: 'none', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '1.5' }} />
                                            </svg>
                                        </div>
                                        <div className="extra-info">
                                            <span className="extra-title">{t('double_back', 'Double Back')}</span>
                                            <span className="extra-desc">{t('enhanced_durability', 'Enhanced durability')}</span>
                                        </div>
                                    </button>
                                    <button
                                        className={`extra-option-button ultrasonic ${extras.ultrasonicCutting ? 'selected' : ''}`}
                                        onClick={() => {
                                            if (extras.ultrasonicCutting && !extras.zemindouble) {
                                                setExtras(prev => ({
                                                    ...prev,
                                                    standard: false,
                                                    ultrasonicCutting: true
                                                }));
                                                return;
                                            }
                                            setExtras(prev => ({
                                                ...prev,
                                                standard: false,
                                                ultrasonicCutting: !prev.ultrasonicCutting
                                            }));
                                        }}
                                    >
                                        <div className="method-icon">
                                            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M6 14V12C6 8.68629 8.68629 6 12 6H36C39.3137 6 42 8.68629 42 12V14" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M32 18V30" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M40 20V28" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M24 15V33" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M16 18V30" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M8 20V28" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                                <path d="M6 34V36C6 39.3137 8.68629 42 12 42H36C39.3137 42 42 39.3137 42 36V34" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </div>
                                        <div className="extra-info">
                                            <span className="extra-title">{t('ultrasonic_cutting', 'Ultrasonic')}</span>
                                            <span className="extra-desc">{t('clean_edges', 'Clean edges')}</span>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                </div>

                <div className="designer-canvas">
                    <div className="preview-header">
                        <div className="preview-info">
                            <span className="preview-title">
                                {labelType === 'woven' ? t('woven_label_preview', 'Woven Label Preview') : t('printed_label_preview', 'Printed Label Preview')}
                            </span>
                            <div className="dimension-info">
                                <span className="actual-size">{`${labelHeight}x${labelWidth}mm`}</span>
                                {(labelType === 'woven' || labelType === 'printed') && applicationMethod !== 'iron-on' && (
                                    <span className="seam-info">{t('seam_allowance_text', '(8+8mm seam allowance)')}</span>
                                )}
                            </div>
                        </div>
                        <span className="preview-subtitle">
                            {labelType === 'printed' ? (
                                t(fabricType, fabricType
                                    .split('-')
                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                    .join(' '))
                            ) : (
                                t(selectedQuality, selectedQuality.charAt(0).toUpperCase() + selectedQuality.slice(1))
                            )}
                        </span>
                    </div>
                    <div className="canvas-frame" onClick={() => setIsPreviewZoomed(true)} style={{ cursor: 'zoom-in' }}>
                        <div
                            ref={labelRef}
                            className="label-preview"
                            style={{
                                width: `${labelWidth * calculateScaleFactor(labelWidth, labelHeight).widthScale}px`,
                                height: `${labelHeight * calculateScaleFactor(labelWidth, labelHeight).heightScale}px`
                            }}
                        >
                            {renderLabelContent()}
                        </div>
                    </div>
                    <div className="order-section desktop-order-section">
                        <div className="quantity-selector">
                            <label htmlFor="quantity">{t('quantity', 'Quantity')}</label>
                            <select
                                id="quantity"
                                value={quantity}
                                onChange={e => setQuantity(Number(e.target.value))}
                                disabled={loading}
                            >
                                {activeQuantities.map(q => (
                                    <option key={q} value={q}>{q} {t('pieces', 'pieces')}</option>
                                ))}
                            </select>
                        </div>
                        {isValid && (
                            <div className="price-info">
                                <div className="total-price">
                                    <span>{t('total_price_label', 'Total Price:')}</span>
                                    <div className="price-with-discount">
                                        <span className="amount">{formatPrice(prices.total)}</span>
                                        {prices.discountRate > 0 && !prices.isMarkup && (
                                            <span className="discount-info">
                                                {prices.discountRate}% {t('off', 'off')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="price-per-piece">{formatPrice(prices.perPiece)} {t('per_piece', 'per piece')}</div>
                            </div>
                        )}
                        <button
                            className={`add-to-cart-button ${loading ? 'loading' : ''}`}
                            onClick={addToCart}
                            disabled={loading || !isValid}
                        >
                            {loading ? t('adding_to_cart', 'Adding to Cart...') : t('add_to_cart', 'Add to Cart')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Order Section - Direct child of designer-container for correct position:fixed */}
            <div className="order-section mobile-order-section">
                <div className="quantity-selector">
                    <label htmlFor="quantity-mobile">{t('quantity', 'Quantity')}</label>
                    <select
                        id="quantity-mobile"
                        value={quantity}
                        onChange={e => setQuantity(Number(e.target.value))}
                        disabled={loading}
                    >
                        {activeQuantities.map(q => (
                            <option key={q} value={q}>{q} {t('pieces', 'pieces')}</option>
                        ))}
                    </select>
                </div>
                {isValid && prices.discountRate > 0 && !prices.isMarkup && (
                    <span className="discount-badge-stuck">
                        -{prices.discountRate}%
                    </span>
                )}
                {isValid && (
                    <div className="price-info">
                        <div className="total-price">
                            <span className="amount">{formatPrice(prices.total)}</span>
                        </div>
                        <div className="price-per-piece">{formatPrice(prices.perPiece)} {t('per_piece', 'per piece')}</div>
                    </div>
                )}
                <button
                    className={`add-to-cart-button ${loading ? 'loading' : ''}`}
                    onClick={addToCart}
                    disabled={loading || !isValid}
                >
                    {loading ? t('adding_to_cart', 'Adding to Cart...') : t('add_to_cart', 'Add to Cart')}
                </button>
            </div>
            {overlay.show && (
                <div className="overlay">
                    <div className="overlay-content">
                        <p>{overlay.message}</p>
                        <div className="overlay-buttons">
                            {overlay.onConfirm ? (
                                <>
                                    <button
                                        className="overlay-button overlay-button-cancel"
                                        onClick={() => setOverlay({ show: false, message: '', onConfirm: null })}
                                    >
                                        {t('cancel', 'Cancel')}
                                    </button>
                                    <button
                                        className="overlay-button overlay-button-confirm"
                                        onClick={() => {
                                            overlay.onConfirm();
                                            setOverlay({ show: false, message: '', onConfirm: null });
                                        }}
                                    >
                                        {t('confirm_anyway', 'Confirm Anyway')}
                                    </button>
                                </>
                            ) : (
                                <button
                                    className="overlay-button"
                                    onClick={() => setOverlay({ show: false, message: '', onConfirm: null })}
                                >
                                    {t('ok', 'OK')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Fullscreen Click-to-Zoom Lightbox Modal */}
            {isPreviewZoomed && createPortal(
                <div
                    className="preview-lightbox-overlay"
                    onClick={() => setIsPreviewZoomed(false)}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(15, 15, 15, 0.85)',
                        backdropFilter: 'blur(8px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 999999,
                        cursor: 'zoom-out'
                    }}
                >
                    <div
                        className="lightbox-preview-container"
                        onClick={() => setIsPreviewZoomed(false)}
                        style={{
                            transformOrigin: 'center center',
                            display: 'flex',
                            gap: '24px',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <div
                            className="label-preview lightbox-card-zoom"
                            style={{
                                width: `${labelWidth * calculateScaleFactor(labelWidth, labelHeight).widthScale}px`,
                                height: `${labelHeight * calculateScaleFactor(labelWidth, labelHeight).heightScale}px`
                            }}
                        >
                            {renderLabelContent()}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Fullscreen Blocking Add-to-Cart Loader Spinner */}
            {loading && createPortal(
                <div
                    className="global-blocking-loader"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        backdropFilter: 'blur(4px)',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 99999999,
                        pointerEvents: 'all'
                    }}
                >
                    <div className="global-loading-spinner" style={{
                        width: '50px',
                        height: '50px',
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #2563eb',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '16px'
                    }} />
                    <span style={{ fontSize: '16px', fontWeight: '600', color: '#111827', fontFamily: 'sans-serif' }}>
                        {t('adding_to_cart', 'Adding to cart...')}
                    </span>
                </div>,
                document.body
            )}
        </div>
    );
}